import type { StreamStatus } from '../types';

export interface ReconnectingSocketOptions {
  /** Endpoints tried in order; after a failed connect the next one is used. */
  urls: string[];
  /** Called on every (re)connect; send subscriptions here. */
  onOpen?: (send: (data: string) => void) => void;
  onMessage: (data: string) => void;
  onStatus?: (status: StreamStatus) => void;
  /** Application-level keep-alive some venues require (e.g. `{"op":"ping"}` every 20 s). */
  heartbeat?: { intervalMs: number; message: string | (() => string) };
  /** Reconnect when nothing arrived for this long. Default 60 s. */
  staleAfterMs?: number;
  WebSocket?: typeof WebSocket;
}

const MAX_BACKOFF_MS = 30_000;

/**
 * A WebSocket that reconnects with exponential backoff, resubscribes through
 * `onOpen`, keeps venue heartbeats going and recycles silent connections.
 * Inert (status 'error', no throw) where no WebSocket implementation exists.
 */
export class ReconnectingSocket {
  private socket: WebSocket | null = null;
  private closed = false;
  private attempt = 0;
  private urlIndex = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private staleTimer: ReturnType<typeof setInterval> | null = null;
  private lastMessageAt = 0;

  constructor(private readonly options: ReconnectingSocketOptions) {
    this.connect();
  }

  send(data: string): void {
    if (this.socket && this.socket.readyState === 1) this.socket.send(data);
  }

  close(): void {
    this.closed = true;
    this.clearTimers();
    if (this.socket) {
      this.socket.onopen = null;
      this.socket.onmessage = null;
      this.socket.onerror = null;
      this.socket.onclose = null;
      try {
        this.socket.close();
      } catch {
        // Closing a socket that never opened can throw in some browsers; it is gone either way.
      }
      this.socket = null;
    }
    this.options.onStatus?.('closed');
  }

  private connect(): void {
    if (this.closed) return;
    const Impl = this.options.WebSocket ?? (globalThis as { WebSocket?: typeof WebSocket }).WebSocket;
    if (!Impl) {
      this.options.onStatus?.('error');
      return;
    }
    const url = this.options.urls[this.urlIndex % this.options.urls.length];
    this.options.onStatus?.('connecting');
    let socket: WebSocket;
    try {
      socket = new Impl(url);
    } catch {
      this.scheduleReconnect(true);
      return;
    }
    this.socket = socket;
    let opened = false;

    socket.onopen = () => {
      opened = true;
      this.attempt = 0;
      this.lastMessageAt = Date.now();
      this.options.onStatus?.('open');
      this.options.onOpen?.((data) => this.send(data));
      this.startTimers();
    };
    socket.onmessage = (event: MessageEvent) => {
      this.lastMessageAt = Date.now();
      if (typeof event.data !== 'string') return;
      try {
        this.options.onMessage(event.data);
      } catch {
        // A malformed frame must not kill the stream; the next frame is independent.
      }
    };
    socket.onerror = () => {
      this.options.onStatus?.('error');
    };
    socket.onclose = () => {
      this.clearTimers();
      this.socket = null;
      if (!this.closed) this.scheduleReconnect(!opened);
    };
  }

  private scheduleReconnect(failedToOpen: boolean): void {
    if (this.closed) return;
    if (failedToOpen) this.urlIndex += 1;
    const delay = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** this.attempt) + Math.random() * 500;
    this.attempt += 1;
    this.reconnectTimer = setTimeout(() => this.connect(), delay);
  }

  private startTimers(): void {
    const { heartbeat } = this.options;
    if (heartbeat) {
      this.heartbeatTimer = setInterval(() => {
        this.send(typeof heartbeat.message === 'function' ? heartbeat.message() : heartbeat.message);
      }, heartbeat.intervalMs);
    }
    const staleAfter = this.options.staleAfterMs ?? 60_000;
    this.staleTimer = setInterval(() => {
      if (Date.now() - this.lastMessageAt > staleAfter) {
        // Silent connection: drop it and let onclose schedule the reconnect.
        try {
          this.socket?.close();
        } catch {
          this.socket = null;
          this.scheduleReconnect(false);
        }
      }
    }, Math.min(staleAfter, 15_000));
  }

  private clearTimers(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    if (this.staleTimer) clearInterval(this.staleTimer);
    this.reconnectTimer = null;
    this.heartbeatTimer = null;
    this.staleTimer = null;
  }
}
